import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import { MoviefoneTVTPOutput } from 'types/moviefone';
import { SkewPlotResponse } from 'gen/stream-api/requests';
import { getEMA, getTVTPColor, pct0 } from 'util/moviefoneFormat';

const clickableSx = {
  cursor: 'pointer',
  '&:hover': { backgroundColor: 'action.hover' },
};

export const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        borderRadius: '8px',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'border-color 0.2s',
        '&:hover': { borderColor: theme.palette.divider },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: '14px',
          py: '10px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.action.hover,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: '11.5px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent
        sx={{ p: 0, flexGrow: 1, '&:last-child': { paddingBottom: 0 } }}
      >
        {children}
      </CardContent>
    </Card>
  );
};

export type KVRow = { label: string; value: string };

export const KVTable = ({
  rows,
  section,
  onAsk,
  valueColorFn,
}: {
  rows: KVRow[];
  section: string;
  onAsk?: (msg: string) => void;
  valueColorFn?: (label: string, value: string) => string | undefined;
}) => {
  const theme = useTheme();
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          borderCollapse: 'collapse',
          '& td': {
            px: '14px',
            py: '6px',
            fontSize: '0.75rem',
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <TableBody>
          {rows.map((r) => (
            <TableRow
              key={r.label}
              sx={onAsk != null ? clickableSx : undefined}
              onClick={() =>
                onAsk?.(
                  `Explain "${r.label}" in the ${section} section. Its current value is: ${r.value}. What does this mean and what are its implications?`,
                )
              }
            >
              <TableCell
                sx={{
                  color: theme.palette.text.primary,
                  whiteSpace: 'nowrap',
                  width: '55%',
                  fontWeight: 500,
                }}
              >
                {r.label}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  color:
                    valueColorFn?.(r.label, r.value) ??
                    theme.palette.text.primary,
                }}
              >
                {r.value}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const MiniTable = <T,>({
  headers,
  rows,
  section,
  onAsk,
  columnColorFns,
  rawData,
}: {
  headers: string[];
  rows: string[][];
  section?: string;
  onAsk?: (msg: string) => void;
  columnColorFns?: ((
    value: string,
    row: string[],
    colIndex: number,
    rawItem?: T,
  ) => string | undefined)[];
  rawData?: T[];
}) => {
  const theme = useTheme();
  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          borderCollapse: 'collapse',
          '& td, & th': {
            px: 1,
            py: '5px',
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '& th': {
            backgroundColor: theme.palette.action.hover,
            fontWeight: 600,
            fontSize: '10.5px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: theme.palette.text.secondary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {headers.map((h) => (
              <TableCell key={h}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow
              key={i}
              sx={onAsk != null ? clickableSx : undefined}
              onClick={() =>
                onAsk?.(
                  `Explain this row in the ${section} section: ${headers.map((h, j) => `${h}: ${row[j]}`).join(', ')}. What does it mean and what are its implications?`,
                )
              }
            >
              {row.map((cell, j) => (
                <TableCell
                  key={j}
                  sx={{
                    color:
                      columnColorFns?.[j]?.(cell, row, j, rawData?.[i]) ??
                      theme.palette.text.primary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const SkewPlotSection = ({
  skewData,
}: {
  skewData: SkewPlotResponse;
}) => {
  const theme = useTheme();

  const formatIV = (v: number) => `${(v * 100).toFixed(1)}%`;

  const colors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
  ];

  const spot = skewData.expirations[0]?.spot ?? 0;

  // Only show the curve with the largest DTE
  const maxDteExp = skewData.expirations.reduce(
    (a, b) => (a.dte > b.dte ? a : b),
    skewData.expirations[0],
  );
  const expirationsEMA = [maxDteExp].map((exp) => ({
    ...exp,
    ivs: getEMA(exp.ivs),
  }));

  const combinedData =
    expirationsEMA[0]?.strikes.map((strike, i) => {
      const point: Record<string, number> = { strike };
      expirationsEMA.forEach((exp) => {
        point[`iv_${exp.dte}`] = exp.ivs[i];
      });
      return point;
    }) ?? [];

  // Use only the largest DTE expiration for domain calculations
  const allStrikes = maxDteExp.strikes;
  const fwdStrikes = skewData.forward_vol_data?.strikes ?? [];
  const allIVs = maxDteExp.ivs;
  const xDomain = [
    Math.min(...allStrikes, ...fwdStrikes),
    Math.max(...allStrikes, ...fwdStrikes),
  ];
  const yDomain = [
    Math.min(...allIVs) - 0.02,
    Math.min(Math.max(...allIVs) + 0.02, 4.0),
  ];

  return (
    <Stack sx={{ gap: 1 }}>
      <Box sx={{ height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={combinedData}
            margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis
              dataKey="strike"
              type="number"
              tick={{ fontSize: 10 }}
              domain={xDomain}
              tickFormatter={(v: number) => v.toFixed(0)}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickFormatter={(v: number) => (v * 100).toFixed(0)}
              domain={yDomain}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatIV(value),
                name.replace('iv_', 'DTE: '),
              ]}
              labelFormatter={(label: number) => `Strike: ${label}`}
              contentStyle={{
                fontSize: '11px',
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
              }}
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <ReferenceLine
              x={spot}
              stroke={theme.palette.info.main}
              strokeDasharray="5 5"
              label={{
                value: 'ATM',
                position: 'top',
                fontSize: 9,
                fill: theme.palette.info.main,
              }}
            />
            {expirationsEMA.map((exp, idx) => (
              <Line
                key={exp.dte}
                type="monotone"
                dataKey={`iv_${exp.dte}`}
                stroke={colors[idx % colors.length]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                name={`DTE: ${exp.dte}`}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
      {skewData.forward_vol_data != null && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
            Forward Vol Curve ({skewData.forward_vol_data.front_expiration} →{' '}
            {skewData.forward_vol_data.back_expiration}, DTE:{' '}
            {skewData.forward_vol_data.forward_dte})
          </Typography>
          <Box sx={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={(() => {
                  const smoothedIVs = getEMA(
                    skewData.forward_vol_data.forward_ivs,
                  );
                  return skewData.forward_vol_data.strikes
                    .slice(1)
                    .map((strike, i) => ({
                      strike,
                      fwdIV: smoothedIVs[i + 1],
                    }));
                })()}
                margin={{ top: 5, right: 10, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="strike"
                  type="number"
                  tick={{ fontSize: 10 }}
                  domain={xDomain}
                  tickFormatter={(v: number) => v.toFixed(0)}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: number) => (v * 100).toFixed(0)}
                  domain={[
                    (dataMin: number) => dataMin - 0.02,
                    (dataMax: number) => Math.min(dataMax + 0.02, 4.0),
                  ]}
                />
                <Tooltip
                  formatter={(value: number) => [formatIV(value), 'Fwd IV']}
                  labelFormatter={(label: number) => `Strike: ${label}`}
                  contentStyle={{
                    fontSize: '11px',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                <ReferenceLine
                  x={skewData.expirations[0]?.spot ?? 0}
                  stroke={theme.palette.info.main}
                  strokeDasharray="5 5"
                  label={{
                    value: 'ATM',
                    position: 'top',
                    fontSize: 9,
                    fill: theme.palette.info.main,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="fwdIV"
                  stroke={theme.palette.secondary.main}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export const TVTPRegimeBars = ({ tvtp }: { tvtp: MoviefoneTVTPOutput }) => {
  const theme = useTheme();
  return (
    <Stack sx={{ gap: 0.5, p: 0.5 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: getTVTPColor(tvtp.regime, theme),
          }}
        >
          {tvtp.regime}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.primary }}
        >
          Confidence: {pct0(tvtp.confidence)}
        </Typography>
      </Box>
      {/* Probability bars */}
      {(['LOW_VOL', 'NORMAL', 'HIGH_VOL', 'CRISIS'] as const).map(
        (label, idx) => {
          const prob = tvtp.regime_probabilities[idx];
          return (
            <Box key={label}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.25 }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.65rem',
                    fontFamily: 'monospace',
                  }}
                >
                  {pct0(prob)}
                </Typography>
              </Stack>
              <Box
                sx={{
                  height: 6,
                  backgroundColor: 'transparent',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${prob * 100}%`,
                    backgroundColor: getTVTPColor(label, theme),
                    borderRadius: 1,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
            </Box>
          );
        },
      )}
    </Stack>
  );
};
